import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Pencil, Trash2, X, Save, Sparkles, Loader2, ImageIcon, Tag, MapPin, User, FileText, Wand2, Clock, Share2, HelpCircle, Target } from "lucide-react";
import { toast } from "sonner";
import GooglePreview from "./GooglePreview";
import ImageUploader from "./ImageUploader";
import RichEditor from "../../components/admin/RichEditor";

const empty = {
  title: "", excerpt: "", content: "", category: "actualites",
  cover_image: "", cover_alt: "", gallery: [],
  author: "Kloub'Air Plomberie", author_bio: "", reading_time: 0,
  tags: [], related_services: [], related_cities: [],
  cta_title: "", cta_text: "",
  faq: [],
  published: true, published_at: "",
  meta_title: "", meta_description: "", og_image: "",
  slug: "", keyword: "",
};

function scoreBlogSEO(data) {
  let score = 0; const tips = [];
  const kw = (data.keyword || "").toLowerCase().trim();
  const words = (data.content || "").split(/\s+/).filter(Boolean).length;

  if (data.meta_title && data.meta_title.length >= 30 && data.meta_title.length <= 65) score += 12; else tips.push("Meta title 30-65 car.");
  if (data.meta_description && data.meta_description.length >= 120 && data.meta_description.length <= 165) score += 12; else tips.push("Meta description 120-165 car.");
  if (words >= 600) score += 15; else if (words >= 300) score += 8; else tips.push("Contenu ≥ 600 mots");
  if ((data.content || "").match(/^##\s/m)) score += 6; else tips.push("Structurer avec des ## H2");
  if ((data.content || "").match(/^###\s/m)) score += 4;
  if (data.cover_image && data.cover_alt) score += 8; else tips.push("Cover + ALT text");
  if (data.excerpt && data.excerpt.length >= 80) score += 6; else tips.push("Extrait ≥ 80 car.");
  if (data.tags?.length >= 3) score += 6; else tips.push("≥ 3 tags longue traîne");
  if (data.related_services?.length || data.related_cities?.length) score += 6; else tips.push("Maillage interne (services/villes)");
  if (data.faq?.length >= 2) score += 6; else tips.push("≥ 2 questions FAQ");
  if (kw) {
    const hits = ["meta_title", "meta_description", "content", "title", "excerpt"].filter(k => (data[k] || "").toLowerCase().includes(kw)).length;
    if (hits >= 4) score += 10; else tips.push(`Utiliser "${data.keyword}" dans 4+ champs`);
    // density
    if (words > 200) {
      const occ = ((data.content || "").toLowerCase().match(new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length;
      const density = (occ / words) * 100;
      if (density >= 0.5 && density <= 2.5) score += 5;
      else if (density > 2.5) tips.push("Mot-clé trop présent (>2.5%)");
      else tips.push("Densité mot-clé 0.5-2.5%");
    }
  } else tips.push("Définir un mot-clé cible");
  if (data.og_image) score += 4;
  return { score: Math.min(100, score), tips, words };
}

export default function BlogAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await api.get("/blog", { params: { published: undefined } })).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.title) return toast.error("Titre obligatoire");
    setSaving(true);
    try {
      const body = {
        ...edit,
        tags: Array.isArray(edit.tags) ? edit.tags : (edit.tags || "").split(",").map(s => s.trim()).filter(Boolean),
        gallery: Array.isArray(edit.gallery) ? edit.gallery : (edit.gallery || "").split("\n").map(s => s.trim()).filter(Boolean),
        related_services: Array.isArray(edit.related_services) ? edit.related_services : (edit.related_services || "").split(",").map(s => s.trim()).filter(Boolean),
        related_cities: Array.isArray(edit.related_cities) ? edit.related_cities : (edit.related_cities || "").split(",").map(s => s.trim()).filter(Boolean),
        faq: Array.isArray(edit.faq) ? edit.faq : [],
      };
      if (edit.id) await api.put(`/blog/${edit.id}`, body);
      else await api.post("/blog", body);
      toast.success("Article enregistré"); setEdit(null); load();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const aiGenerate = async () => {
    if (!edit.keyword && !edit.title) return toast.error("Saisissez un mot-clé ou un titre d'abord");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/seo-generate", {
        target_type: "blog", keyword: edit.keyword, city: "Grans",
        prompt: edit.title || edit.keyword,
      });
      const next = {
        ...edit,
        title: data.title || edit.title,
        meta_title: data.meta_title || edit.meta_title,
        meta_description: data.meta_description || edit.meta_description,
        slug: data.slug || edit.slug,
        excerpt: data.excerpt || data.short_description || edit.excerpt,
        cover_alt: data.cover_alt || edit.cover_alt,
        content: data.content || edit.content,
        cta_title: data.cta_title || edit.cta_title,
        cta_text: data.cta_text || edit.cta_text,
      };
      if (data.faq?.length && (!edit.faq || !edit.faq.length)) next.faq = data.faq;
      if (Array.isArray(data.tags) && data.tags.length) {
        next.tags = data.tags;
      }
      setEdit(next);
      toast.success("Contenu IA généré !");
    } catch { toast.error("Génération IA échouée"); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-blog-page">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">CMS · SEO Premium</div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Blog</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Articles optimisés SEO avec score temps réel, FAQ, maillage interne, OpenGraph.</p>
        </div>
        <Button onClick={() => setEdit({ ...empty })} className="bg-[color:var(--brand-red)]" data-testid="new-blog-btn">
          <Plus className="w-4 h-4 mr-1" />Nouvel article
        </Button>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => {
          const seo = p.seo?.score ?? 0;
          return (
            <Card key={p.id} className="overflow-hidden border shadow-hover">
              {p.cover_image && <img src={p.cover_image} alt={p.cover_alt || p.title} className="h-40 w-full object-cover" />}
              <div className="p-4">
                <div className="flex items-center gap-1.5 flex-wrap mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] text-[10px] font-bold uppercase">{p.category}</span>
                  {p.reading_time > 0 && <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono">{p.reading_time} min</span>}
                  {!p.published && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase">Brouillon</span>}
                </div>
                <h3 className="font-display font-bold text-[color:var(--brand-navy)] line-clamp-2 mb-2">{p.title}</h3>
                <div className="flex items-center justify-between mt-2">
                  <span className={`px-2 py-0.5 rounded-full font-mono font-bold text-[10px] ${seo >= 80 ? "bg-emerald-100 text-emerald-700" : seo >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    SEO {seo}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => setEdit(p)}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="outline" size="sm" onClick={async () => {
                      if (!window.confirm("Supprimer cet article ?")) return;
                      await api.delete(`/blog/${p.id}`); load();
                    }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {edit && <BlogEditor edit={edit} setEdit={setEdit} save={save} saving={saving} aiGenerate={aiGenerate} aiLoading={aiLoading} />}
    </div>
  );
}

function BlogEditor({ edit, setEdit, save, saving, aiGenerate, aiLoading }) {
  const normalized = useMemo(() => ({
    ...edit,
    tags: Array.isArray(edit.tags) ? edit.tags : (edit.tags || "").split(",").map(s => s.trim()).filter(Boolean),
    faq: Array.isArray(edit.faq) ? edit.faq : [],
  }), [edit]);
  const seo = useMemo(() => scoreBlogSEO(normalized), [normalized]);
  const set = (k, v) => setEdit({ ...edit, [k]: v });

  const [fieldLoading, setFieldLoading] = useState(null);
  const aiField = async (field, apply) => {
    if (!edit.keyword && !edit.title && field !== "title") {
      return toast.error("Saisissez d'abord un mot-clé ou un titre pour orienter l'IA");
    }
    setFieldLoading(field);
    try {
      const { data } = await api.post("/ai/blog-field", {
        field,
        keyword: edit.keyword || "",
        title: edit.title || "",
        category: edit.category || "actualites",
        excerpt: edit.excerpt || "",
        content: edit.content || "",
      });
      if (data.value === undefined || data.value === null || (Array.isArray(data.value) && data.value.length === 0)) {
        toast.error("L'IA n'a pas renvoyé de valeur — réessayez avec plus de contexte");
        return;
      }
      apply(data.value);
      toast.success("✨ Champ généré par l'IA");
    } catch {
      toast.error("Génération IA échouée");
    } finally {
      setFieldLoading(null);
    }
  };

  const addFaq = () => set("faq", [...(edit.faq || []), { question: "", answer: "" }]);
  const updateFaq = (i, field, value) => {
    const next = [...(edit.faq || [])];
    next[i] = { ...next[i], [field]: value };
    set("faq", next);
  };
  const removeFaq = (i) => {
    const next = [...(edit.faq || [])]; next.splice(i, 1); set("faq", next);
  };

  const AiBtn = ({ field, apply, label = "IA", title = "Générer avec l'IA", className = "" }) => (
    <button
      type="button"
      onClick={() => aiField(field, apply)}
      disabled={fieldLoading === field}
      title={title}
      data-testid={`ai-field-${field}`}
      className={`shrink-0 inline-flex items-center gap-1 h-9 px-2.5 rounded-md bg-gradient-to-r from-[color:var(--brand-navy)] to-[color:var(--brand-sky)] text-white text-xs font-bold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 ${className}`}
    >
      {fieldLoading === field ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-2 md:p-4 overflow-y-auto" onClick={() => setEdit(null)} data-testid="blog-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl my-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-display font-bold text-xl text-[color:var(--brand-navy)]">{edit.id ? "Éditer" : "Nouvel"} article</h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Article SEO premium — score temps réel, FAQ, maillage interne.</p>
          </div>
          <button onClick={() => setEdit(null)}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* SEO SCORE BANNER */}
          <div className={`rounded-2xl p-4 flex items-center gap-4 ${seo.score >= 80 ? "bg-emerald-50 border border-emerald-200" : seo.score >= 50 ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`}>
            <div className={`w-16 h-16 rounded-full flex items-center justify-center font-display font-black text-xl ${seo.score >= 80 ? "bg-emerald-500 text-white" : seo.score >= 50 ? "bg-amber-500 text-white" : "bg-red-500 text-white"}`}>{seo.score}</div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-display font-bold text-[color:var(--brand-navy)]">Score SEO temps réel</div>
                <span className="text-xs text-[hsl(var(--muted-foreground))]"><Clock className="w-3 h-3 inline mr-0.5" />{Math.max(1, Math.round(seo.words / 200))} min · {seo.words} mots</span>
              </div>
              {seo.tips.length > 0 ? (
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  <strong>Pour progresser :</strong> {seo.tips.slice(0, 3).join(" · ")}
                  {seo.tips.length > 3 && ` · +${seo.tips.length - 3} autres`}
                </div>
              ) : <div className="text-xs text-emerald-700 mt-1 font-semibold">✓ Article optimisé, prêt pour Google.</div>}
            </div>
          </div>

          {/* Base */}
          <Section title="Informations générales" icon={FileText}>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>Titre *</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.title} onChange={e => set("title", e.target.value)} />
                  <AiBtn field="title" apply={(v) => set("title", v)} />
                </div>
              </div>
              <div><Label>Catégorie</Label>
                <Select value={edit.category} onValueChange={v => set("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actualites">Actualités</SelectItem>
                    <SelectItem value="conseils">Conseils</SelectItem>
                    <SelectItem value="etudes-de-cas">Études de cas</SelectItem>
                    <SelectItem value="guides">Guides</SelectItem>
                    <SelectItem value="comparatifs">Comparatifs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Mot-clé SEO cible</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.keyword} onChange={e => set("keyword", e.target.value)} placeholder="installation climatisation" />
                  <Button type="button" onClick={aiGenerate} disabled={aiLoading} className="shrink-0 bg-gradient-to-r from-[color:var(--brand-red)] to-[color:var(--brand-navy)] font-bold" data-testid="ai-generate-blog" title="Générer l'article COMPLET depuis le mot-clé">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" />Article complet</>}
                  </Button>
                </div>
                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">« Article complet » remplit tous les champs d'un coup. Les boutons ✨ IA à côté de chaque champ permettent de régénérer uniquement ce champ.</p>
              </div>
              <div className="md:col-span-2">
                <Label>Extrait / chapô</Label>
                <div className="flex gap-2 mt-1.5">
                  <Textarea rows={2} value={edit.excerpt} onChange={e => set("excerpt", e.target.value)} className="flex-1" />
                  <AiBtn field="excerpt" apply={(v) => set("excerpt", v)} className="self-start" />
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={edit.published} onCheckedChange={v => set("published", v)} id="pub" /><Label htmlFor="pub">Publié</Label></div>
            </div>
          </Section>

          {/* Author */}
          <Section title="Auteur" icon={User}>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Auteur</Label><Input value={edit.author} onChange={e => set("author", e.target.value)} /></div>
              <div><Label>Temps de lecture (min, auto si vide)</Label><Input type="number" min={0} value={edit.reading_time || ""} onChange={e => set("reading_time", parseInt(e.target.value) || 0)} /></div>
              <div className="md:col-span-2">
                <Label>Bio auteur (affichée sous l'article)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.author_bio} onChange={e => set("author_bio", e.target.value)} placeholder="Fondateur de Kloub'Air Plomberie, 10 ans d'expérience." />
                  <AiBtn field="author_bio" apply={(v) => set("author_bio", v)} />
                </div>
              </div>
            </div>
          </Section>

          {/* Content */}
          <Section title="Contenu principal" icon={FileText}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <Label>Corps de l'article</Label>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">Mise en forme WYSIWYG : titres, listes, citations, liens, images depuis la médiathèque.</p>
              </div>
              <AiBtn field="content" apply={(v) => set("content", v)} label="Générer le contenu" title="Générer un article complet de 600–900 mots basé sur le titre et le mot-clé" />
            </div>
            <RichEditor value={edit.content} onChange={(html) => set("content", html)} placeholder="Commencez à écrire votre article…" minHeight={400} />
          </Section>

          {/* Media */}
          <Section title="Médias & OpenGraph" icon={ImageIcon}>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Image de couverture (URL)</Label>
                  <ImageUploader folder="blog" label="Uploader" onUploaded={url => set("cover_image", url)} />
                </div>
                <Input value={edit.cover_image} onChange={e => set("cover_image", e.target.value)} />
              </div>
              <div>
                <Label>ALT text cover *(SEO + accessibilité)*</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.cover_alt} onChange={e => set("cover_alt", e.target.value)} placeholder="Pose d'une climatisation gainable à Grans" />
                  <AiBtn field="cover_alt" apply={(v) => set("cover_alt", v)} />
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Image OpenGraph (partage réseaux, 1200×630)</Label>
                  <ImageUploader folder="blog-og" label="Uploader OG" onUploaded={url => set("og_image", url)} />
                </div>
                <Input value={edit.og_image} onChange={e => set("og_image", e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Galerie complémentaire (URLs — une par ligne)</Label>
                  <ImageUploader folder="blog-gallery" label="Ajouter plusieurs photos" multiple onUploaded={(url) => {
                    setEdit((prev) => {
                      const current = Array.isArray(prev.gallery) ? prev.gallery : (prev.gallery || "").split("\n").filter(Boolean);
                      return { ...prev, gallery: [...current, url] };
                    });
                  }} />
                </div>
                <Textarea rows={3} value={Array.isArray(edit.gallery) ? edit.gallery.join("\n") : edit.gallery} onChange={e => set("gallery", e.target.value)} />
              </div>
            </div>
          </Section>

          {/* Maillage + Tags */}
          <Section title="Maillage & tags (longue traîne)" icon={Tag}>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <Label>Tags (virgules)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={Array.isArray(edit.tags) ? edit.tags.join(", ") : edit.tags} onChange={e => set("tags", e.target.value)} placeholder="clim, économies d'énergie, rénovation" />
                  <AiBtn field="tags" apply={(arr) => set("tags", Array.isArray(arr) ? arr : [])} />
                </div>
              </div>
              <div><Label>Services liés (slugs, virgules)</Label><Input value={Array.isArray(edit.related_services) ? edit.related_services.join(", ") : edit.related_services} onChange={e => set("related_services", e.target.value)} placeholder="pose-climatisation-residentielle" /></div>
              <div><Label>Villes mentionnées (virgules)</Label><Input value={Array.isArray(edit.related_cities) ? edit.related_cities.join(", ") : edit.related_cities} onChange={e => set("related_cities", e.target.value)} placeholder="Grans, Salon-de-Provence" /></div>
            </div>
          </Section>

          {/* CTA */}
          <Section title="Call-to-action fin d'article" icon={Target}>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Titre CTA</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.cta_title} onChange={e => set("cta_title", e.target.value)} placeholder="Un projet de climatisation ? Parlons-en." />
                  <AiBtn field="cta_title" apply={(v) => set("cta_title", v)} />
                </div>
              </div>
              <div>
                <Label>Texte CTA</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.cta_text} onChange={e => set("cta_text", e.target.value)} placeholder="Devis gratuit sous 24h." />
                  <AiBtn field="cta_text" apply={(v) => set("cta_text", v)} />
                </div>
              </div>
              <div>
                <Label>Libellé du bouton CTA</Label>
                <Input value={edit.cta_label || ""} onChange={e => set("cta_label", e.target.value)} placeholder="Demander un devis gratuit" className="mt-1.5" />
              </div>
              <div>
                <Label>Destination du CTA</Label>
                <Select value={edit._cta_mode || (edit.cta_url?.startsWith("http") ? "external" : "internal")} onValueChange={(v) => set("_cta_mode", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="cta-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Page du site</SelectItem>
                    <SelectItem value="external">URL externe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                {(edit._cta_mode || (edit.cta_url?.startsWith("http") ? "external" : "internal")) === "internal" ? (
                  <>
                    <Label>Page du site</Label>
                    <Select value={edit.cta_url || "/devis"} onValueChange={(v) => set("cta_url", v)}>
                      <SelectTrigger className="mt-1.5" data-testid="cta-internal-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="/devis">Demande de devis (/devis)</SelectItem>
                        <SelectItem value="/contact">Contact (/contact)</SelectItem>
                        <SelectItem value="/services">Liste des services (/services)</SelectItem>
                        <SelectItem value="/realisations">Réalisations (/realisations)</SelectItem>
                        <SelectItem value="/blog">Blog (/blog)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Ou saisissez un chemin custom :</p>
                    <Input value={edit.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="/services/pose-climatisation-residentielle" className="mt-1" data-testid="cta-internal-custom" />
                  </>
                ) : (
                  <>
                    <Label>URL externe (https://...)</Label>
                    <Input value={edit.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://calendly.com/kloubair/devis" className="mt-1.5" data-testid="cta-external-input" />
                    <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Le lien s'ouvrira dans un nouvel onglet avec <code>rel="noopener noreferrer"</code>.</p>
                  </>
                )}
              </div>
            </div>
          </Section>

          {/* FAQ */}
          <Section title="FAQ associée (rich snippets Google)" icon={HelpCircle}>
            <div className="flex items-center justify-end mb-2">
              <AiBtn field="faq" apply={(arr) => set("faq", Array.isArray(arr) ? arr : [])} label="Générer 3-5 questions" title="Générer automatiquement une FAQ optimisée" />
            </div>
            <div className="space-y-2">
              {(edit.faq || []).map((f, i) => (
                <div key={i} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 items-start border border-[hsl(var(--border))] rounded-xl p-2.5">
                  <Input placeholder="Question" value={f.question} onChange={e => updateFaq(i, "question", e.target.value)} />
                  <Textarea rows={2} placeholder="Réponse" value={f.answer} onChange={e => updateFaq(i, "answer", e.target.value)} />
                  <Button type="button" variant="outline" size="sm" onClick={() => removeFaq(i)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addFaq} className="w-full" data-testid="add-faq-item">
                <Plus className="w-3 h-3 mr-1" /> Ajouter une question
              </Button>
            </div>
          </Section>

          {/* SEO */}
          <Section title="SEO meta & preview Google" icon={Wand2}>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Meta title ({(edit.meta_title || "").length}/65)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.meta_title} onChange={e => set("meta_title", e.target.value)} />
                  <AiBtn field="meta_title" apply={(v) => set("meta_title", v)} />
                </div>
              </div>
              <div>
                <Label>Slug URL</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={edit.slug} onChange={e => set("slug", e.target.value)} />
                  <AiBtn field="slug" apply={(v) => set("slug", v)} />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Meta description ({(edit.meta_description || "").length}/165)</Label>
                <div className="flex gap-2 mt-1.5">
                  <Textarea rows={2} value={edit.meta_description} onChange={e => set("meta_description", e.target.value)} className="flex-1" />
                  <AiBtn field="meta_description" apply={(v) => set("meta_description", v)} className="self-start" />
                </div>
              </div>
            </div>
            <GooglePreview title={edit.meta_title || edit.title} description={edit.meta_description} slug={edit.slug} path="blog" />
          </Section>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t">
          <Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)] font-bold" data-testid="save-blog">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : <><Save className="w-4 h-4 mr-1" />Enregistrer</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] p-4">
      <div className="flex items-center gap-2 text-[color:var(--brand-navy)] font-display font-bold mb-3">
        <Icon className="w-4 h-4 text-[color:var(--brand-sky)]" />
        {title}
      </div>
      {children}
    </div>
  );
}
